import { BridgeMarshalledDatasource, HasuraBaseQuery, NULL_DATA_STRING } from '@anduin/shared';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { getReasonInfo, getPendingDisplayStatus } from '../../../utils/unsent-email-reason.utils';
import { PendingEmailRow, PendingEmailStatus, RawPendingRow } from '../../../models';

export class PendingEmailDatasource extends BridgeMarshalledDatasource<RawPendingRow, PendingEmailRow> {
  overdueHours = 24;

  constructor(apollo: Apollo, baseQuery: HasuraBaseQuery) {
    super(
      apollo,
      baseQuery,
      (raw: RawPendingRow) => this.marshallRow(raw),
    );
  }

  getUndismissedCount(): Observable<number> {
    const entityName = this.baseQuery.entityName;
    const countQueryStr = `query { ${entityName}_aggregate(where: { dismissed_at: { _is_null: true }, resolved_at: { _is_null: true } }) { aggregate { count } } }`;

    return this.apollo
      .query<Record<string, { aggregate: { count: number } }>>({
        query: gql(countQueryStr),
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data[`${entityName}_aggregate`].aggregate.count),
        take(1),
      );
  }

  pollRetryingStatuses(ids: number[]): Observable<{ id: number; status: PendingEmailStatus }[]> {
    const entityName = this.baseQuery.entityName;
    const queryStr = `
      query PollRetryingStatuses($ids: [bigint!]!) {
        ${entityName}(where: { id: { _in: $ids } }) {
          id
          status
        }
      }
    `;

    return this.apollo
      .query<Record<string, { id: number; status: PendingEmailStatus }[]>>({
        query: gql(queryStr),
        variables: { ids },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data[entityName]),
        take(1),
      );
  }

  private marshallRow(raw: RawPendingRow): PendingEmailRow {
    const info = getReasonInfo(raw.reasonCode);

    const diffMs = Date.now() - new Date(raw.createdAt).getTime();
    const totalMinutes = Math.floor(diffMs / 60_000);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);
    const totalMonths = Math.floor(totalDays / 30);
    const totalYears = Math.floor(totalDays / 365);

    const pluralize = (count: number) => (count === 1 ? '' : 's');

    let timeInQueue: string;
    if (totalYears > 0) {
      timeInQueue = `${totalYears} year${pluralize(totalYears)}`;
    } else if (totalMonths > 0) {
      timeInQueue = `${totalMonths} month${pluralize(totalMonths)}`;
    } else if (totalDays > 0) {
      timeInQueue = `${totalDays} day${pluralize(totalDays)}`;
    } else if (totalHours > 0) {
      timeInQueue = `${totalHours} hour${pluralize(totalHours)}`;
    } else {
      timeInQueue = `${totalMinutes} minute${pluralize(totalMinutes)}`;
    }

    return {
      id: raw.id,
      status: raw.status,
      pendingDisplayStatus: getPendingDisplayStatus(raw.status, raw.createdAt, this.overdueHours),
      reasonCode: raw.reasonCode,
      reasonDisplay: info.displayText,
      recommendedAction: info.recommendedAction,
      subject: raw.subject || NULL_DATA_STRING,
      recipient: raw.recipient || '',
      recipientCc: raw.recipientCc || '',
      recipientBcc: raw.recipientBcc || '',
      allRecipients: raw.allRecipients || '',
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
      dismissedAt: raw.dismissedAt,
      resolvedAt: raw.resolvedAt,
      timeInQueue,
    };
  }
}
